// Tipos para sistema de Tools/Ferramentas dos Assistentes

export interface Tool {
  id: string;
  name: string;
  description: string;
  icon: string;
  provider: 'google_calendar' | 'google_drive' | 'web_search' | 'saipos' | 'custom_mcp';
  category: 'productivity' | 'files' | 'communication' | 'search' | 'restaurant' | 'custom';
  operations: ToolOperation[];
  requiresAuth: boolean;
  isEnabled: boolean;
}

export interface ToolOperation {
  id: string;
  name: string;
  description: string;
  parameters: ToolParameter[];
  returnType: string;
}

export interface ToolParameter {
  name: string;
  type: 'string' | 'number' | 'boolean' | 'date' | 'datetime' | 'array' | 'object';
  description: string;
  required: boolean;
  default?: any;
}

export interface ToolCall {
  id: string;
  toolId: string;
  operationId: string;
  parameters: Record<string, any>;
  status: 'pending' | 'running' | 'completed' | 'failed';
  result?: any;
  error?: string;
  startedAt: Date;
  completedAt?: Date;
}

export interface AssistantTool {
  toolId: string;
  enabledOperations: string[]; // IDs das operações habilitadas
  isActive: boolean;
}

// Definições de Tools disponíveis
export const AVAILABLE_TOOLS: Tool[] = [
  {
    id: 'google_calendar',
    name: 'Google Calendar',
    description: 'Gerenciar eventos e calendários do Google',
    icon: '/google-calendar-logo.png',
    provider: 'google_calendar',
    category: 'productivity',
    requiresAuth: true,
    isEnabled: true,
    operations: [
      {
        id: 'create_event',
        name: 'Criar Evento',
        description: 'Cria um novo evento no calendário',
        parameters: [
          { name: 'summary', type: 'string', description: 'Título do evento', required: true },
          { name: 'description', type: 'string', description: 'Descrição do evento', required: false },
          { name: 'start', type: 'datetime', description: 'Data/hora de início', required: true },
          { name: 'end', type: 'datetime', description: 'Data/hora de término', required: true },
          { name: 'attendees', type: 'array', description: 'Lista de e-mails dos participantes', required: false },
          { name: 'location', type: 'string', description: 'Local do evento', required: false },
          { name: 'calendarId', type: 'string', description: 'ID do calendário (padrão: primary)', required: false }
        ],
        returnType: 'CalendarEvent'
      },
      {
        id: 'delete_event',
        name: 'Deletar Evento',
        description: 'Remove um evento do calendário',
        parameters: [
          { name: 'eventId', type: 'string', description: 'ID do evento', required: true },
          { name: 'calendarId', type: 'string', description: 'ID do calendário (padrão: primary)', required: false }
        ],
        returnType: 'boolean'
      },
      {
        id: 'update_event',
        name: 'Atualizar Evento',
        description: 'Atualiza um evento existente',
        parameters: [
          { name: 'eventId', type: 'string', description: 'ID do evento', required: true },
          { name: 'summary', type: 'string', description: 'Novo título do evento', required: false },
          { name: 'description', type: 'string', description: 'Nova descrição', required: false },
          { name: 'start', type: 'datetime', description: 'Nova data/hora de início', required: false },
          { name: 'end', type: 'datetime', description: 'Nova data/hora de término', required: false },
          { name: 'attendees', type: 'array', description: 'Nova lista de participantes', required: false },
          { name: 'location', type: 'string', description: 'Novo local', required: false },
          { name: 'calendarId', type: 'string', description: 'ID do calendário (padrão: primary)', required: false }
        ],
        returnType: 'CalendarEvent'
      },
      {
        id: 'list_events',
        name: 'Listar Eventos',
        description: 'Lista eventos do calendário',
        parameters: [
          { name: 'calendarId', type: 'string', description: 'ID do calendário (padrão: primary)', required: false },
          { name: 'timeMin', type: 'datetime', description: 'Data mínima', required: false },
          { name: 'timeMax', type: 'datetime', description: 'Data máxima', required: false },
          { name: 'maxResults', type: 'number', description: 'Número máximo de eventos', required: false, default: 10 },
          { name: 'query', type: 'string', description: 'Busca por texto', required: false }
        ],
        returnType: 'CalendarEvent[]'
      },
      {
        id: 'list_calendars',
        name: 'Listar Calendários',
        description: 'Lista todos os calendários disponíveis',
        parameters: [],
        returnType: 'Calendar[]'
      },
      {
        id: 'find_free_slots',
        name: 'Encontrar Horários Livres',
        description: 'Encontra slots onde todos os participantes estão livres',
        parameters: [
          { name: 'attendees', type: 'array', description: 'E-mails dos participantes', required: true },
          { name: 'duration', type: 'number', description: 'Duração em minutos', required: true },
          { name: 'timeMin', type: 'datetime', description: 'Data mínima para busca', required: true },
          { name: 'timeMax', type: 'datetime', description: 'Data máxima para busca', required: true }
        ],
        returnType: 'FreeSlot[]'
      }
    ]
  },
  {
    id: 'google_drive',
    name: 'Google Drive',
    description: 'Acessar e gerenciar arquivos do Google Drive',
    icon: '/google-drive-logo.png',
    provider: 'google_drive',
    category: 'files',
    requiresAuth: true,
    isEnabled: true,
    operations: [
      {
        id: 'list_files',
        name: 'Listar Arquivos',
        description: 'Lista arquivos do Drive',
        parameters: [
          { name: 'query', type: 'string', description: 'Busca por nome', required: false },
          { name: 'folderId', type: 'string', description: 'ID da pasta', required: false },
          { name: 'maxResults', type: 'number', description: 'Número máximo', required: false, default: 10 }
        ],
        returnType: 'DriveFile[]'
      },
      {
        id: 'select_file',
        name: 'Selecionar Arquivo',
        description: 'Abre o seletor de arquivos do Google Drive',
        parameters: [],
        returnType: 'DriveFile'
      }
    ]
  },
  {
    id: 'web_search',
    name: 'Busca na Web',
    description: 'Busca informações atualizadas na internet',
    icon: '🔍',
    provider: 'web_search',
    category: 'search',
    requiresAuth: false,
    isEnabled: true,
    operations: [
      {
        id: 'search',
        name: 'Buscar',
        description: 'Busca informações atualizadas na web sobre qualquer assunto',
        parameters: [
          { name: 'query', type: 'string', description: 'Termo de busca', required: true }
        ],
        returnType: 'SearchResult[]'
      }
    ]
  },
  {
    id: 'saipos',
    name: 'Saipos',
    description: 'Consultar vendas, itens da venda, estoque e cardápio no Saipos',
    icon: '/saipos-logo.png',
    provider: 'saipos',
    category: 'restaurant',
    requiresAuth: true,
    isEnabled: true,
    operations: [
      {
        id: 'get_sales',
        name: 'Consultar Vendas',
        description: 'Consulta vendas realizadas nas lojas',
        parameters: [
          { name: 'store_id', type: 'string', description: 'ID da loja (opcional)', required: false },
          { name: 'sale_id', type: 'string', description: 'ID da venda específica (opcional)', required: false },
          { name: 'date_from', type: 'date', description: 'Data inicial (formato YYYY-MM-DD)', required: false },
          { name: 'date_to', type: 'date', description: 'Data final (formato YYYY-MM-DD)', required: false },
          { name: 'status', type: 'string', description: 'Status da venda (opcional)', required: false },
          { name: 'customer_document', type: 'string', description: 'CPF/CNPJ do cliente (opcional)', required: false },
          { name: 'limit', type: 'number', description: 'Número máximo de resultados', required: false, default: 100 },
          { name: 'offset', type: 'number', description: 'Offset para paginação', required: false, default: 0 }
        ],
        returnType: 'Sale[]'
      },
      {
        id: 'get_sale_items',
        name: 'Consultar Itens da Venda',
        description: 'Consulta os itens/produtos das vendas no período. No retorno use: quantity (quantidade), unit_price (valor), total vendido = quantity × unit_price.',
        parameters: [
          { name: 'date_from', type: 'date', description: 'Data inicial (formato YYYY-MM-DD)', required: false },
          { name: 'date_to', type: 'date', description: 'Data final (formato YYYY-MM-DD)', required: false },
          { name: 'date_column_filter', type: 'string', description: 'Coluna de data (opcional; padrão: shift_date)', required: false },
          { name: 'limit', type: 'number', description: 'Número máximo de resultados', required: false, default: 300 },
          { name: 'offset', type: 'number', description: 'Offset para paginação', required: false, default: 0 }
        ],
        returnType: 'SaleItem[]'
      },
      {
        id: 'get_stock_position',
        name: 'Estoque',
        description: 'Consulta a posição atual de estoque de ingredientes (API: máx. 15 dias; use days: 7 para última semana)',
        parameters: [
          { name: 'store_id', type: 'string', description: 'ID da loja (opcional)', required: false },
          { name: 'ingredient_id', type: 'string', description: 'ID do ingrediente (opcional)', required: false },
          { name: 'days', type: 'number', description: 'Últimos N dias (1 a 15; 7 = última semana)', required: false }
        ],
        returnType: 'StockPosition[]'
      },
      {
        id: 'get_catalog',
        name: 'Consultar Cardápio',
        description: 'Consulta o cardápio/catálogo (produtos e categorias)',
        parameters: [],
        returnType: 'Catalog'
      }
    ]
  }
];

// Tipos auxiliares para resultados das operações
export interface CalendarEvent {
  id: string;
  summary: string;
  description?: string;
  start: { dateTime: string; timeZone?: string };
  end: { dateTime: string; timeZone?: string };
  attendees?: { email: string; responseStatus?: string }[];
  location?: string;
  htmlLink?: string;
}

export interface Calendar {
  id: string;
  summary: string;
  description?: string;
  primary?: boolean;
  backgroundColor?: string;
}

export interface FreeSlot {
  start: string;
  end: string;
}

export interface StockMovement {
  id?: string;
  store_id?: string;
  ingredient_id?: string;
  ingredient_name?: string;
  movement_type?: string;
  quantity?: number;
  unit?: string;
  date?: string;
  reason?: string;
  [key: string]: any;
}

export interface StockPosition {
  store_id?: string;
  ingredient_id?: string;
  ingredient_name?: string;
  current_stock?: number;
  unit?: string;
  [key: string]: any;
}

export interface Sale {
  id?: string;
  store_id?: string;
  sale_number?: string;
  sale_date?: string;
  customer_name?: string;
  customer_document?: string;
  total?: number;
  status?: string;
  payment_method?: string;
  [key: string]: any;
}

export interface SaleItem {
  id?: string;
  sale_id?: string;
  product_id?: string;
  product_name?: string;
  quantity?: number;
  unit_price?: number;
  total?: number;
  [key: string]: any;
}

export interface SaleStatusHistory {
  id?: string;
  sale_id?: string;
  status?: string;
  status_date?: string;
  user?: string;
  notes?: string;
  [key: string]: any;
}

export interface FinancialEntry {
  id?: string;
  store_id?: string;
  entry_date?: string;
  entry_type?: string;
  description?: string;
  amount?: number;
  category?: string;
  payment_method?: string;
  [key: string]: any;
}

